import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import UserProfileHeader from './UserProfileHeader';
import "../Styles/UserManagement.css";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const YubiKeyDashboard = () => {
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openReassignDialog, setOpenReassignDialog] = useState(false);
    const [reassignCandidates, setReassignCandidates] = useState([]);
    const [selectedCandidateEmail, setSelectedCandidateEmail] = useState('');
    const [reassigningSerial, setReassigningSerial] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    const keysStatusMap = {
        1: 'Pending Assignment',
        2: 'Pending Activation',
        3: 'Activated',
        4: 'Lost',
        5: 'Damaged',
        6: 'Deleted',
    };

    const formatFetchedKeys = (keys) => {
        return (keys || []).map((key) => ({
            ...key,
            status: keysStatusMap[key.status] || key.status,
        }));
    };

    const fetchKeys = () => {
        Axios.post(`/get_keys`).then((response) => {
            if (response.data.success) {
                setUsers(formatFetchedKeys(response.data.data));
            } else {
                toast.error("Failed to get all users.", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
        });
    };


const fetchNoKeyCustomers = async () => {
    try {
        const res = await Axios.post('/get_customers_no_key', {});
        if (res.data?.success) {
            setReassignCandidates(res.data.data || []);
        } else {
            toast.error(res.data?.msg || 'Failed to load customers.', { position: 'top-right' });
        }
    } catch (e) {
        console.error(e);
        toast.error('Network or server error while loading customers.', { position: 'top-right' });
    }
};

const openReassign = async (serialNumber) => {
    setReassigningSerial(serialNumber);
    setSelectedCandidateEmail('');
    setOpenReassignDialog(true);
    await fetchNoKeyCustomers();
};

const handleConfirmReassign = async () => {
    if (!reassigningSerial) {
        toast.error('Missing YubiKey serial number.', { position: 'top-right' });
        return;
    }
    if (!selectedCandidateEmail) {
        toast.error('Please select a customer.', { position: 'top-right' });
        return;
    }
    try {
        const res = await Axios.post('/reassign_yubikey_to_customer', {
            serial_number: reassigningSerial,
            customer_email: selectedCandidateEmail,
            operator_email: localStorage.getItem("userEmail"),
        });
        if (res.data?.success) {
            toast.success(res.data?.msg || 'Reassigned.', { position: 'top-right' });
            setOpenReassignDialog(false);
            setReassigningSerial('');
            setSelectedCandidateEmail('');
            fetchKeys();
        } else {
            toast.error(res.data?.msg || 'Failed to reassign.', { position: 'top-right' });
        }
    } catch (e) {
        console.error(e);
        toast.error('Network or server error while reassigning.', { position: 'top-right' });
    }
};

    useEffect(() => {
        fetchKeys();
    }, []);

    const handleSelectUser = (userEmail) => {
        setSelectedUsers((prev) => {
            if (prev.includes(userEmail)) {
                return prev.filter((email) => email !== userEmail);
            } else {
                return [...prev, userEmail];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(user => user.serial_number));
        }
        setSelectAll(!selectAll);
    };

    const handleDeleteUsers = async () => {
        try {
            const res = await Axios.post(`/delete_velox_cloud_keys`, {
                serial_numbers: selectedUsers,                 // <-- serial numbers
                operator_email: localStorage.getItem("userEmail"), // for velox_logs
            });

            if (res.data?.success) {
                toast.success(`${res.data.msg}`, {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                });
            } else {
                toast.error(res.data?.msg || "Failed to delete VELOX Cloud Keys.", {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                });
            }

            fetchKeys();
            setSelectedUsers([]);
            setOpenDialog(false);
            setSelectAll(false);
        } catch (e) {
            toast.error("Network or server error while deleting.", {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
            });
        }
    };

    const handleBack = () => {
        console.log("Back button clicked!");
        console.log("Current path:", location.pathname);
        // Dismiss any active toasts before navigating
        toast.dismiss();
        // Check if we can go back in history
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);  // Go back one step in history
            console.log("Navigating back one step.");
        } else {
            // If no history, navigate to the dashboard without adding an entry
            navigate('/dashboard', { replace: true });
            console.log("No history to go back, redirecting to dashboard.");
        }
    };

    return (
        <div className="dashboard">
            <UserProfileHeader isAdmin={true} />

            <div className="all-users-card">
                <h1 className="Title-alluser">VELOX Cloud Key Dashboard</h1>

                <div className="button-row">
                    <button onClick={fetchKeys} className="refresh-button">Refresh</button>
                    <button onClick={handleBack} className="save-button">Back</button>
                    <button
                        onClick={() => setOpenDialog(true)}
                        className="delete-button"
                        disabled={selectedUsers.length === 0}
                    >
                        Delete
                    </button>
                </div>

                <div className="table-container">
                    <table className="styled-table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th>Order</th>
                                <th>YubiKey Serial Number</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>Company Name</th>
                                <th>MD ID</th>
                                <th>SI ID</th>
                                <th>Status</th>
                                <th>Edit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((yk, index) => (
                                <tr key={yk.serial_number}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(yk.serial_number)}
                                            onChange={() => handleSelectUser(yk.serial_number)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td>{yk.serial_number}</td>
                                    <td>{yk.first_name}</td>
                                    <td>{yk.last_name}</td>
                                    <td>{yk.email}</td>
                                    <td>{yk.company_name}</td>
                                    <td>{yk.md_id}</td>
                                    <td>{yk.si_id}</td>
                                    <td>{yk.status}</td>
                                    <td>
                                        {yk.status === "Pending Assignment" ? (
                                            <button
                                                className="refresh-button"
                                                onClick={() => openReassign(yk.serial_number)}
                                            >
                                                Reassign
                                            </button>
                                        ) : (
                                            <span className="status-readonly">-</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>


<Dialog
    open={openReassignDialog}
    onClose={() => setOpenReassignDialog(false)}
    aria-labelledby="reassign-dialog-title"
    fullWidth
    maxWidth="md"
>
    <DialogTitle id="reassign-dialog-title">{"Reassign YubiKey"}</DialogTitle>
    <DialogContent>
        <DialogContentText>
            Select a customer (Status: No Key Assigned) to assign this YubiKey:
            <strong> {reassigningSerial || "-"}</strong>
        </DialogContentText>

        <div className="table-container" style={{ marginTop: "12px" }}>
            <table className="styled-table">
                <thead>
                    <tr>
                        <th></th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Company Name</th>
                        <th>MD ID</th>
                    </tr>
                </thead>
                <tbody>
                    {reassignCandidates.map((c) => (
                        <tr key={c.email}>
                            <td>
                                <input
                                    type="radio"
                                    name="reassignCustomer"
                                    checked={selectedCandidateEmail === c.email}
                                    onChange={() => setSelectedCandidateEmail(c.email)}
                                />
                            </td>
                            <td>{c.first_name || "-"}</td>
                            <td>{c.last_name || "-"}</td>
                            <td>{c.email}</td>
                            <td>{c.company_name || "-"}</td>
                            <td>{c.md_id || "-"}</td>
                        </tr>
                    ))}
                    {reassignCandidates.length === 0 && (
                        <tr>
                            <td colSpan={6} style={{ textAlign: "center" }}>
                                No customers with "No Key Assigned".
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    </DialogContent>
    <DialogActions>
        <Button onClick={handleConfirmReassign} color="primary">Confirm</Button>
        <Button onClick={() => setOpenReassignDialog(false)} color="secondary" autoFocus>Cancel</Button>
    </DialogActions>
</Dialog>

                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Delete Users"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Do you want to delete these VELOX Cloud Keys?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteUsers} color="primary">Yes</Button>
                        <Button onClick={() => setOpenDialog(false)} color="secondary" autoFocus>No</Button>
                    </DialogActions>
                </Dialog>
            </div>

            <ToastContainer />
        </div>
    );

};

export default YubiKeyDashboard;
